import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import userManager from '../auth/oidcConfig';
import { getUserByUsername } from '../services/authentikAPI';
import TopBanner from '../components/TopBanner';
import { wasRecentlySynced } from '../auth/sessionSyncMarker';
import { navigateAway } from '../lib/tabCloseSuppression';

//const INSPECTION_APP_URL = 'https://script.google.com/macros/s/AKfycbyxPAqH7NHOcivwKX31EZPlYQBLjOlusEgRC2jwZCIn5YNnYuu3qY9P-i5tp4flEGRKGA/exec';
const INSPECTION_APP_URL = 'https://script.google.com/macros/s/AKfycbw1XVmVW1w0ajO-FXHOEqgFWlTrAVInP98x2GPv7ZZJ_6rf4EHPnC1tFzJTKtGz2cfu/exec';
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';

const page = {
  minHeight: '100vh',
  boxSizing: 'border-box',
  backgroundImage: 'url(/BESCOM_SSO_BG_Image.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
};

// Used for the Profile/Admin dropdown menus — these need to stay legible
// no matter what's behind them, so mostly-opaque rather than a true glass
// effect (just enough gradient at the top edge to keep some sheen).
const glassCard = {
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.94) 0%, rgba(255, 255, 255, 0.86) 100%)',
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  borderTop: '1px solid rgba(255, 255, 255, 0.9)',
  borderRadius: '16px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
};

// Darker, more reflective — and much more transparent — variant for the
// main "Your Applications" panel. Low opacity throughout so the
// background image actually shows through; the diagonal highlight-to-dark
// gradient is what reads as "mirror" rather than a flat tinted pane.
const glassCardDark = {
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.16) 0%, rgba(8, 20, 45, 0.22) 45%, rgba(8, 20, 45, 0.30) 100%)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  border: '1px solid rgba(255, 255, 255, 0.25)',
  borderTop: '1px solid rgba(255, 255, 255, 0.55)',
  borderRadius: '16px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
};
// Two-person "swap" icon for the Transfer app — a person in each corner
// with a pair of arrows looping between them, echoing the classic
// "employee transfer / exchange" pictogram rather than the generic 🔄
// placeholder every other app card still uses.
function TransferIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* top-right person */}
      <circle cx="46" cy="15" r="6" fill="#5B5B5B" />
      <rect x="34" y="24" width="24" height="16" rx="8" fill="#A9C6EA" />
      <path d="M46 24l-3 3 3 9 3-9z" fill="#D97B7B" />
      {/* bottom-left person */}
      <circle cx="18" cy="41" r="6" fill="#D9A066" />
      <rect x="6" y="50" width="24" height="13" rx="7" fill="#9AD1A6" />
      <path d="M18 50l-3 3 3 8 3-8z" fill="#EFC75E" />
      {/* arrow: top-right down to bottom-left */}
      <path d="M36 8H16v14" stroke="#1A1A1A" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11 17l5 5 5-5" stroke="#1A1A1A" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      {/* arrow: bottom-left up to top-right */}
      <path d="M28 58h20V44" stroke="#1A1A1A" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M43 49l5-5 5 5" stroke="#1A1A1A" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Icons that can't be represented in the backend's plain-JSON app store
// (apps.js) — looked up by id and rendered instead of whatever `icon`
// string comes back from the API for that app.
const ICON_OVERRIDES = {
  transfer: <TransferIcon />,
};

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [apps, setApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [showRejectedPopup, setShowRejectedPopup] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/apps`)
      .then((res) => res.json())
      .then((data) => setApps(data.apps || []))
      .catch((err) => console.error('Failed to load applications', err))
      .finally(() => setAppsLoading(false));
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.profile?.preferred_username) {
        setProfileLoading(false);
        return;
      }

      try {
        const data = await getUserByUsername(user.profile.preferred_username);
        const attributes = data?.attributes || {};
        setProfile({
          name: user.profile?.name || data?.username || user.profile?.preferred_username,
          email: user.profile?.email || '',
          mobile: attributes?.contact_phone || '',
          designation: attributes?.designation || '',
          zone: attributes?.zone || '',
          circle: attributes?.circle || '',
          division: attributes?.division || '',
          subdivision: attributes?.subdivision || '',
          section: attributes?.section || '',
          profile_complete: attributes?.profile_complete || user.profile?.profile_complete || false,
          approval_status: attributes?.approval_status || null,
          is_active: data?.is_active !== false,
        });
      } catch (err) {
        console.error('Failed to load Authentik profile', err);
      } finally {
        setProfileLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  // While someone sits on the "pending approval" waiting screen below, an
  // admin rejecting them elsewhere previously went unnoticed here entirely
  // — profile was only ever loaded once, on mount, so nothing updated
  // until a manual refresh (which then bounced them to the login page's
  // banner instead of explaining it right here, where they're actually
  // waiting). Polls specifically for this transition and surfaces a clear
  // popup in place, rather than relying on the person to refresh into a
  // redirect to find out why.
  useEffect(() => {
    if (profile?.approval_status !== 'pending') return undefined;
    const username = user?.profile?.preferred_username;
    if (!username) return undefined;

    const interval = setInterval(async () => {
      try {
        const data = await getUserByUsername(username);
        const status = data?.attributes?.approval_status || null;
        if (status === 'rejected' || data?.is_active === false) {
          setProfile((prev) => ({ ...prev, approval_status: status, is_active: data?.is_active !== false }));
          setShowRejectedPopup(true);
        }
      } catch (err) {
        // Best-effort — a network blip just means this check waits for
        // the next tick, not a hard failure.
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [profile?.approval_status, user]);

  // Registration sets bescom_needs_session_sync instead of redirecting
  // through Authentik's recovery flow immediately (see Register.jsx) — do
  // that redirect here instead, once the profile has actually loaded and
  // there are real apps to use it for. Skipped entirely while pending
  // approval (nothing to sync toward yet); the flag just stays set and
  // this fires on a later visit once approved.
  //
  // CONFIRMED BUG (2026-08-15): this only excluded 'pending', not
  // 'rejected'/deactivated — a rejected account still had this flag set
  // from registration, so visiting here fired a REAL top-level navigation
  // to Authentik's own session-sync link. Authentik's non-interactive
  // Login stage does correctly refuse a deactivated account
  // (ak-stage-access-denied), but by then the browser has already left
  // this app's origin, landing on Authentik's own raw "Request has been
  // denied" flow-executor page instead of anything we control. Excluding
  // rejected/deactivated here too means that redirect never fires in the
  // first place.
  useEffect(() => {
    if (!profile || profile.approval_status === 'pending') return;
    if (profile.approval_status === 'rejected' || profile.is_active === false) return;
    if (localStorage.getItem('bescom_needs_session_sync') !== '1') return;

    localStorage.removeItem('bescom_needs_session_sync');

    // CONFIRMED ROOT CAUSE (2026-09-03) — see sessionSyncMarker.js. This is
    // exactly the path a SECOND consecutive fresh login takes (the flag
    // above gets set by every login in submitStep's normal branch): without
    // this check, that second login's own sync mints another Authentik
    // grant for this client, superseding the first login's still-"most
    // recent" one and triggering a Back-Channel Logout notification this
    // app's receiver mishandles as revoking the brand-new session too —
    // logging the browser back out within seconds of the second login.
    // Skipping when a sync already succeeded recently avoids minting that
    // second grant in the first place.
    if (wasRecentlySynced()) return;

    fetch(`${API_BASE}/account/session-sync-link`, {
      method: 'POST',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.sessionSyncUrl) {
          navigateAway(data.sessionSyncUrl);
        }
      })
      .catch(() => {
        // Best-effort — cross-app SSO just won't be instant this time;
        // a normal login later still sets it up fine.
      });
  }, [profile]);

  if (loading || profileLoading || appsLoading) {
    return (
      <div style={{ ...page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ ...glassCard, padding: '32px 40px' }}>
          <p style={{ margin: 0, fontSize: '16px', color: '#666' }}>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    navigate('/');
    return null;
  }

  const userGroups = user.profile?.groups || [];
  // Absent (undefined/null) is treated as approved, not pending — this
  // attribute never existed before this feature, and every pre-existing
  // account must not suddenly get locked out just because it was never set.
  const isPendingApproval = profile?.approval_status === 'pending';
  // Defense in depth, independent of the token-validity chain (session-
  // status/watchdog) — even if a stale-but-technically-unexpired session
  // somehow got this far, this page's own fresh profile fetch still
  // reflects the real, current state and refuses to show real app access
  // for it. See adminApprovals.js's /reject for the other half of this
  // fix (actually revoking the session, not just this display gate).
  const isRejected = profile?.approval_status === 'rejected' || profile?.is_active === false;

  const accessibleApps = apps.filter(app =>
    app.enabled !== false && (app.groups || []).some(g => userGroups.includes(g))
  );

  return (
    <div style={page}>
      <TopBanner />

      {/* App Grid */}
      <div style={{ padding: '40px 32px' }}>
        <div style={{ ...glassCardDark, padding: '32px' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#fff', marginBottom: '8px' }}>
              Your Applications
            </h2>
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.75)', marginBottom: '32px' }}>
              Click on any application to open
            </p>
          </div>

          {isRejected ? (
            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.75)', padding: '20px' }}>
              <p style={{ fontSize: '32px', margin: '0 0 16px' }}>🚫</p>
              <p style={{ fontWeight: '600', color: '#fff', marginBottom: '4px' }}>
                Your account has been rejected
              </p>
              <p>Contact your administrator if you believe this is incorrect.</p>
            </div>
          ) : isPendingApproval ? (
            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.75)', padding: '20px' }}>
              <p style={{ fontSize: '32px', margin: '0 0 16px' }}>⏳</p>
              <p style={{ fontWeight: '600', color: '#fff', marginBottom: '4px' }}>
                Your account is pending approval
              </p>
              <p>A Super Admin needs to sign off on your registration before your applications become available. Check back shortly.</p>
            </div>
          ) : accessibleApps.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.75)', padding: '20px' }}>
              <p style={{ fontSize: '32px', margin: '0 0 16px' }}>🔒</p>
              <p>No applications assigned yet. Contact your administrator.</p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '24px'
            }}>
              {accessibleApps.map(app => (
              <div
                key={app.id}
                style={{
                  background: '#B9B8B3', borderRadius: '16px',
                  padding: '28px', cursor: 'pointer',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  borderTop: `4px solid ${app.color}`
                }}
                onClick={() => {
                  // Do not gate app launch based on SSO profile_complete here.
                  // Individual applications can enforce their own requirements if needed.
                  if (app.id === 'inspection') {
                    userManager.getUser().then(async oidcUser => {
                      if (!oidcUser) { navigate('/'); return; }
                      try {
                        const res = await fetch(`${API_BASE}/profile/app-token`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ access_token: oidcUser.access_token })
                        });
                        const data = await res.json();
                        if (!data.success) { navigate('/'); return; }
                        window.open(`${app.url}?token=${encodeURIComponent(data.app_token)}`, '_blank');
                      } catch (err) {
                        console.error('Token exchange failed:', err);
                        navigate('/');
                      }
                    });
                  } else if (app.sso) {
                    // Deep-link into this app's own Login page with a
                    // marker, rather than building Authentik's authorize
                    // URL ourselves. The target app's own
                    // userManager.signinRedirect() must be what initiates
                    // the OIDC round-trip, so its oidc-client-ts stores the
                    // matching PKCE/state data its own Callback.jsx expects
                    // to validate on return. If the browser already holds a
                    // live Authentik session, this still ends up feeling
                    // instant — no separate manual click required.
                    window.open(`${app.url}/login?sso=auto`, '_blank');
                  } else {
                    window.open(app.url, '_blank');
                  }
                }}
                onMouseOver={e => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.3)';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.18)';
                }}
              >
                <div style={{ fontSize: '40px', marginBottom: '16px' }}>{ICON_OVERRIDES[app.id] || app.icon}</div>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 8px' }}>
                  {app.name}
                </h3>
                <p style={{ fontSize: '13px', color: 'rgba(26,26,26,0.7)', margin: '0 0 20px' }}>
                  {app.description}
                </p>
                <div style={{
                  display: 'inline-block', padding: '6px 16px',
                  background: app.color, color: '#fff',
                  borderRadius: '6px', fontSize: '13px', fontWeight: '500'
                }}>
                  Open →
                </div>
              </div>
            ))}
            </div>
          )}
        </div>
      </div>

      {showRejectedPopup && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(20, 24, 33, 0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px', zIndex: 2000,
        }}>
          <div style={{
            background: '#fff', borderRadius: '14px', padding: '32px',
            width: '100%', maxWidth: '380px', textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
          }}>
            <p style={{ fontSize: '32px', margin: '0 0 12px' }}>🚫</p>
            <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 10px' }}>
              Your account has been rejected
            </h3>
            <p style={{ fontSize: '14px', color: '#5F5E5A', margin: '0 0 24px', lineHeight: 1.5 }}>
              Please contact your administrator.
            </p>
            <button
              type="button"
              onClick={() => logout()}
              style={{
                padding: '10px 32px', background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
                color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
